import { Avatar, Chip, ListItem, Stack, Typography } from "@mui/material";
import { playerHandleSuffix } from "@bw/shared/components";
import type { LeaderboardEntryResponse } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";
import { TierBadge } from "./TierBadge";
import { MISSING, ratingLabel, UNRATED } from "./competeDisplay";

/** "Alex Lin" → "AL"; one word → its first letter. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  return letters.map((word) => word.charAt(0).toUpperCase()).join("");
}

export function LadderRow({
  row,
  position,
  isSelf = false,
}: {
  row: LeaderboardEntryResponse;
  position: number;
  isSelf?: boolean;
}) {
  const hasName = row.display_name != null && row.display_name !== "";
  const name = hasName ? (row.display_name as string) : MISSING;
  return (
    <ListItem
      aria-current={isSelf ? "true" : undefined}
      data-self={isSelf || undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: "1.5rem 2.5rem minmax(0, 1fr) auto",
        alignItems: "center",
        gap: 1.25,
        px: 1,
        py: 0.75,
        borderRadius: "12px",
        bgcolor: isSelf ? "action.selected" : undefined,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {position}
      </Typography>
      <Avatar
        aria-hidden
        sx={{ width: 36, height: 36, fontSize: "0.875rem", fontWeight: 700 }}
      >
        {hasName ? initials(name) : "?"}
      </Avatar>
      <Stack sx={{ minWidth: 0 }}>
        {hasName ? (
          <Typography
            component={RouterLink}
            to={`/players/${row.user_id}`}
            sx={{
              color: "text.primary",
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {playerHandleSuffix(row.user_id)}
            </Typography>
          </Typography>
        ) : (
          <Typography>{name}</Typography>
        )}
        {isSelf ? (
          <Chip
            label="You"
            size="small"
            color="primary"
            sx={{ alignSelf: "flex-start" }}
          />
        ) : null}
      </Stack>
      <Stack sx={{ alignItems: "flex-end", gap: 0.25 }}>
        {row.is_placed ? (
          <Typography sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }}>
            {ratingLabel(row.rating)}
          </Typography>
        ) : null}
        {row.is_placed ? (
          <TierBadge tier={row.tier} division={row.division} placed />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {UNRATED}
          </Typography>
        )}
      </Stack>
    </ListItem>
  );
}
